<template>
  <div id="keyword-title">
    <div class="page-header">
      <h2 class="page-title">제목 진단</h2>
      <p class="page-desc">
        쓰려는 제목을 넣으면 <strong>검색 수요·경쟁·형식</strong>을 100점으로 채점하고,
        더 나은 제목 후보를 제안합니다.
      </p>
    </div>

    <section class="search-card">
      <div class="seed-row">
        <input
          v-model="title" class="seed-input" type="text"
          placeholder="예: 여의도 점심 삼계탕 맛집 다녀온 후기"
          maxlength="120" @keyup.enter="analyze"
        />
        <button class="btn-search" :disabled="loading" @click="analyze">
          {{ loading ? "분석 중…" : "진단" }}
        </button>
      </div>
      <p class="hint-line">
        제목을 그대로 붙여 넣으세요. 문장에서 단어를 뽑아 실제 검색량과 대조합니다.
      </p>
    </section>

    <div v-if="errorMessage" class="notice notice-error">{{ errorMessage }}</div>
    <!-- 제목 낱말을 조합해 검색량을 하나씩 확인하느라 10초 안팎 걸린다 -->
    <div v-if="loading" class="loading-panel">
      <div class="lp-bar"><i></i></div>
      <div class="lp-stage">{{ stage }}</div>
      <div class="lp-meta">
        <span class="lp-elapsed">{{ elapsed }}초 경과</span>
        <span class="lp-hint">보통 10~15초 걸립니다</span>
      </div>
      <p class="lp-note">
        제목의 낱말을 <b>서로 조합해</b> 실제 검색량이 있는지 하나씩 확인합니다.
        연관 목록에 없는 키워드도 이렇게 찾아냅니다.
      </p>
    </div>

    <template v-else-if="result">
      <div v-if="result.quotaWarning" class="notice notice-warn">{{ result.quotaWarning }}</div>

      <!-- ───────── 1부: 입력한 제목 진단 ───────── -->
      <div class="part-head">
        <span class="ph-no">1</span>
        <span class="ph-text">입력한 제목 진단</span>
        <span class="ph-sub">지금 제목이 어떤 상태인가</span>
      </div>

      <!-- 점수 -->
      <section class="score-card" :class="gradeClass">
        <div class="sc-left">
          <div class="sc-score">{{ result.score }}<span>/100</span></div>
          <div class="sc-grade">{{ result.grade }}</div>
        </div>
        <div class="sc-right">
          <div class="sc-title">{{ result.title }}</div>
          <div class="sc-summary">{{ result.summary }}</div>
        </div>
      </section>

      <!-- 항목별 -->
      <section class="detail-card">
        <div class="dc-title">항목별 점수</div>
        <div v-for="it in result.items" :key="it.label" class="score-row">
          <div class="sr-head">
            <span class="sr-label">{{ it.label }}</span>
            <span class="sr-num">{{ it.score }} / {{ it.max }}</span>
          </div>
          <div class="sr-track">
            <div class="sr-fill" :class="barClass(it.score, it.max)"
                 :style="{ width: (it.score / it.max * 100) + '%' }"></div>
          </div>
          <p class="sr-comment">{{ it.comment }}</p>
        </div>
      </section>

      <!-- 제목이 담은 키워드 -->
      <section class="detail-card">
        <div class="dc-title">이 제목이 담고 있는 검색 키워드</div>
        <div v-if="result.matched && result.matched.length" class="matched-list">
          <div v-for="m in result.matched" :key="m.keyword" class="mt-item">
            <a class="mt-kw" href="#" @click.prevent="goSearch(m.keyword)">{{ m.keyword }}</a>
            <span class="mt-vol">월 {{ m.volume.toLocaleString() }}회</span>
            <span v-if="m.saturation !== null" class="mt-sat" :class="satClass(m.saturation)">
              포화도 {{ m.saturation.toFixed(2) }}
            </span>
            <span class="mt-pos">{{ m.position === 0 ? "제목 맨 앞" : "앞에서 " + m.position + "번째 글자" }}</span>
          </div>
        </div>
        <p v-else class="dc-note">
          제목에서 실제로 검색되는 키워드를 찾지 못했습니다.
          아래 추천 제목처럼 사람들이 검색하는 표현을 넣어 보세요.
        </p>
      </section>

      <!-- ───────── 2부: 대신 쓸 제목 ───────── -->
      <div class="part-head">
        <span class="ph-no">2</span>
        <span class="ph-text">대신 쓸 제목</span>
        <span class="ph-sub">검색되는 키워드로 다시 지은 후보</span>
      </div>

      <!-- 넓은 화면에서는 두 갈래를 나란히, 폰에서는 위아래로 -->
      <div class="suggest-grid">

      <!-- 추천 제목 -->
      <section class="detail-card">
        <div class="dc-title">키워드 중심 후보</div>
        <p class="dc-note top">
          <b>검색어를 앞세운 짧은 제목</b>입니다. 노출을 먼저 노릴 때 씁니다.
          그대로 쓰기보다 <b>내 글 내용에 맞게 고쳐</b> 쓰세요.
        </p>
        <ul class="suggest-list">
          <li v-for="s in result.suggestions" :key="s.title" class="sg-item">
            <div class="sg-title">
              <span v-if="s.category" class="sg-cat">{{ s.category }}</span>{{ s.title }}
            </div>
            <div class="sg-meta">
              <span class="sg-kw">{{ s.baseKeyword }}</span>
              <span class="sg-vol">월 {{ s.volume.toLocaleString() }}회</span>
              <button class="sg-copy" @click="copy(s.title)">복사</button>
            </div>
            <div class="sg-reason">{{ s.reason }}</div>
          </li>
        </ul>
        <p v-if="!result.suggestions || !result.suggestions.length" class="dc-note">
          추천할 만한 키워드를 찾지 못했습니다. 더 일반적인 단어를 포함해 보세요.
        </p>
      </section>

      <!-- 문장형 제목 -->
      <section class="detail-card ai-card">
        <div class="dc-title">문장형 후보</div>
        <p class="dc-note top">
          지금 제목의 <b>약점과 노릴 키워드</b>를 반영해 상위 노출을 노리는 문장으로 지었습니다.
          갈래(정리·경험·비교·질문·주의·방법)가 겹치지 않게 만듭니다.
        </p>

        <div v-if="result.aiMessage" class="notice notice-warn">{{ result.aiMessage }}</div>

        <ul v-if="result.aiSuggestions && result.aiSuggestions.length" class="suggest-list">
          <li v-for="s in result.aiSuggestions" :key="s.title" class="sg-item"
              :class="{ weak: !s.baseKeyword }">
            <div class="sg-title">
              <span v-if="s.category" class="sg-cat">{{ s.category }}</span>{{ s.title }}
            </div>
            <div class="sg-meta">
              <span v-if="s.baseKeyword" class="sg-kw">{{ s.baseKeyword }}</span>
              <span v-if="s.baseKeyword" class="sg-vol">월 {{ s.volume.toLocaleString() }}회</span>
              <span v-else class="sg-none">검색 키워드 없음</span>
              <button class="sg-copy" @click="copy(s.title)">복사</button>
            </div>
            <div class="sg-reason">{{ s.reason }}</div>
          </li>
        </ul>
      </section>

      </div><!-- /suggest-grid -->
    </template>

    <div v-else-if="!loading && !errorMessage" class="empty-state">
      쓰려는 제목을 입력하고 <b>진단</b>을 눌러 보세요.
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import * as keywordApi from "@/keyword/api";

/**
 * 마지막 진단 결과 보관 자리.
 *
 * <p>탭을 닫으면 사라지는 sessionStorage 를 쓴다 — 검색 이력과 달리 진단은 "지금 쓰고 있는 글"에
 * 딸린 것이라 오래 남길 값이 아니다. 여기에도 발행량·포화도가 들어 있으므로 서버에는 두지 않는다
 * (오픈API 약관 — keyword/utils/history.ts 주석 참고).
 */
const LAST_KEY = "keyword.title.last.v1";

export default defineComponent({
  name: "KeywordTitleView",
  data() {
    return {
      title: "",
      loading: false,
      errorMessage: "",
      result: null as any,
      elapsed: 0,
      timer: null as number | null,
    };
  },
  created() {
    // 다른 메뉴에 다녀와도 진단 결과가 남아 있게 한다(사용자 요청).
    // 다시 진단하면 검색광고·오픈API 호출이 또 나가므로 아까운 일이기도 하다.
    try {
      const raw = sessionStorage.getItem(LAST_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.title && saved.result) {
          this.title = saved.title;
          this.result = saved.result;
        }
      }
    } catch (e) {
      /* 복원 실패는 무시 — 빈 화면으로 시작하면 그만이다 */
    }
  },
  computed: {
    /** 경과 시간으로 짐작한 단계 안내 (네이버가 진행률을 주지 않는다) */
    stage(): string {
      if (this.elapsed < 3) return "제목을 낱말로 나누는 중입니다";
      if (this.elapsed < 9) return "낱말 조합의 검색량을 확인하는 중입니다";
      if (this.elapsed < 16) return "제목 후보를 짓는 중입니다";
      return "발행량을 확인하고 점수를 매기는 중입니다";
    },
    gradeClass(): string {
      const s = this.result?.score ?? 0;
      if (s >= 80) return "good";
      if (s >= 65) return "mid-good";
      if (s >= 45) return "mid";
      return "bad";
    },
  },
  beforeUnmount() {
    this.stopElapsed();
  },
  methods: {
    async analyze() {
      const t = this.title.trim();
      if (!t) {
        this.errorMessage = "제목을 입력해 주세요.";
        return;
      }
      this.loading = true;
      this.errorMessage = "";
      this.startElapsed();
      try {
        const res: any = await keywordApi.analyzeTitle(t);
        const payload = res?.data;
        if (payload && typeof payload.score === "number") {
          this.result = payload;
          try {
            sessionStorage.setItem(LAST_KEY, JSON.stringify({ title: t, result: payload }));
          } catch (e) {
            /* 저장 실패는 무시 */
          }
        } else if (res?.status === 403) {
          this.errorMessage = "키워드 리서치 사용 권한이 없습니다.";
          this.result = null;
        } else {
          this.errorMessage = res?.message || payload?.message || "분석에 실패했습니다.";
          this.result = null;
        }
      } catch (e: any) {
        this.errorMessage = e?.message || "분석 중 오류가 발생했습니다.";
        this.result = null;
      } finally {
        this.loading = false;
        this.stopElapsed();
      }
    },
    startElapsed() {
      this.stopElapsed();
      this.elapsed = 0;
      this.timer = window.setInterval(() => { this.elapsed += 1; }, 1000);
    },
    stopElapsed() {
      if (this.timer !== null) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    },
    goSearch(keyword: string) {
      this.$router.push({ path: "/keyword/search", query: { q: keyword } });
    },
    copy(text: string) {
      navigator.clipboard.writeText(text).then(
        () => alert("제목을 복사했습니다."),
        () => alert("복사에 실패했습니다.")
      );
    },
    barClass(score: number, max: number): string {
      const r = score / max;
      if (r >= 0.75) return "good";
      if (r >= 0.45) return "mid";
      return "bad";
    },
    satClass(s: number): string {
      if (s < 1) return "good";
      if (s < 5) return "mid";
      return "bad";
    },
  },
});
</script>

<style lang="scss" scoped></style>
